import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobApplicationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ScoringPanel from '../components/ScoringPanel';
import InterviewScheduler from '../components/InterviewScheduler';
import EmailComposer from '../components/EmailComposer';
import AdminLayout from '../components/AdminLayout';

const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showScoring, setShowScoring] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);

  useEffect(() => {
    loadApplication();
  }, [id]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      // Use the old API endpoint
      const response = await jobApplicationsAPI.getById(id);
      setApplication(response.data);
    } catch (error) {
      console.error('Failed to load application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (note) => {
    try {
      await jobApplicationsAPI.updateNotes(id, note);
      loadApplication();
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      screening: 'bg-yellow-100 text-yellow-800',
      shortlisted: 'bg-green-100 text-green-800',
      'interview-scheduled': 'bg-purple-100 text-purple-800',
      interviewed: 'bg-indigo-100 text-indigo-800',
      offer: 'bg-pink-100 text-pink-800',
      rejected: 'bg-red-100 text-red-800',
      hired: 'bg-green-200 text-green-900',
      withdrawn: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  if (!application) {
    return (
      <AdminLayout>
        <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">Application not found</p>
          <button
            onClick={() => navigate('/admin/applications')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to Applications
          </button>
        </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/applications')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          ← Back to Applications
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {application.firstName} {application.lastName}
            </h1>
            <p className="text-gray-600 mt-1">{application.email}</p>
            <p className="text-gray-600">{application.phone}</p>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusBadgeColor(application.status)}`}>
              {application.status?.replace('-', ' ')}
            </span>
            <div className="mt-2 text-sm text-gray-500">
              Applied {application.createdAt || application.created_at ? new Date(application.createdAt || application.created_at).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-3">
        <button
          onClick={() => setShowScoring(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Update Scores
        </button>
        <button
          onClick={() => setShowScheduler(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
        >
          Schedule Interview
        </button>
        <button
          onClick={() => setShowEmailComposer(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
        >
          Send Email
        </button>
        {application.resumePath && (
          <a
            href={application.resumePath}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            View Resume
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'scores', 'interviews', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Job Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Position</p>
                  <p className="font-medium">{application.job?.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Department</p>
                  <p className="font-medium">{application.job?.department}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Overall Score</h3>
              <div className="text-4xl font-bold text-blue-600">
                {application.overallScore ? Number(application.overallScore).toFixed(1) : 'N/A'}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Cover Letter</h3>
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.coverLetter || 'No cover letter provided'}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Notes</h3>
              <div className="space-y-2 mb-4">
                {application.notes ? (
                  <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                    {application.notes}
                  </p>
                ) : (
                  <p className="text-gray-500">No notes yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add a note..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      handleAddNote(e.target.value.trim());
                      e.target.value = '';
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = e.target.previousSibling;
                    if (input.value.trim()) {
                      handleAddNote(input.value.trim());
                      input.value = '';
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'scores' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Candidate Scores</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Technical Score</p>
                <div className="flex items-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {application.technicalScore ? Number(application.technicalScore).toFixed(1) : '0.0'}
                  </div>
                  <div className="ml-2 text-sm text-gray-500">/ 5.0</div>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Communication Score</p>
                <div className="flex items-center">
                  <div className="text-2xl font-bold text-green-600">
                    {application.communicationScore ? Number(application.communicationScore).toFixed(1) : '0.0'}
                  </div>
                  <div className="ml-2 text-sm text-gray-500">/ 5.0</div>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Experience Score</p>
                <div className="flex items-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {application.experienceScore ? Number(application.experienceScore).toFixed(1) : '0.0'}
                  </div>
                  <div className="ml-2 text-sm text-gray-500">/ 5.0</div>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Culture Fit Score</p>
                <div className="flex items-center">
                  <div className="text-2xl font-bold text-pink-600">
                    {application.cultureFitScore ? Number(application.cultureFitScore).toFixed(1) : '0.0'}
                  </div>
                  <div className="ml-2 text-sm text-gray-500">/ 5.0</div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-1">Overall Score (Weighted Average)</p>
              <div className="flex items-center">
                <div className="text-4xl font-bold text-blue-600">
                  {application.overallScore ? Number(application.overallScore).toFixed(1) : '0.0'}
                </div>
                <div className="ml-2 text-lg text-gray-500">/ 5.0</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'interviews' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Interview History</h3>
            {application.interviews && application.interviews.length > 0 ? (
              <div className="space-y-4">
                {application.interviews.map((interview) => (
                  <div key={interview.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{interview.title}</h4>
                        <p className="text-sm text-gray-500">
                          {interview.scheduledDate || interview.scheduled_date ? new Date(interview.scheduledDate || interview.scheduled_date).toLocaleDateString() : 'N/A'} at {interview.scheduledTime || interview.scheduled_time || 'N/A'}
                        </p>
                      </div>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        {interview.status}
                      </span>
                    </div>
                    {interview.feedback && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-700">{interview.feedback}</p>
                        {interview.overallRating && (
                          <p className="text-sm text-gray-500 mt-1">
                            Rating: {interview.overallRating}/5.0
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No interviews scheduled yet</p>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
            {application.activities && application.activities.length > 0 ? (
              <div className="space-y-3">
                {application.activities.map((activity) => (
                  <div key={activity.id} className="flex gap-3 pb-3 border-b last:border-0">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
                    <div className="flex-1">
                      <p className="font-medium">{activity.action}</p>
                      {activity.description && (
                        <p className="text-sm text-gray-600">{activity.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {activity.createdAt || activity.created_at ? new Date(activity.createdAt || activity.created_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No activity yet</p>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showScoring && (
        <ScoringPanel
          application={application}
          onClose={() => setShowScoring(false)}
          onUpdate={loadApplication}
        />
      )}
      {showScheduler && (
        <InterviewScheduler
          application={application}
          onClose={() => setShowScheduler(false)}
          onSchedule={loadApplication}
        />
      )}
      {showEmailComposer && (
        <EmailComposer
          application={application}
          onClose={() => setShowEmailComposer(false)}
          onSend={loadApplication}
        />
      )}
      </div>
    </AdminLayout>
  );
};

export default ApplicationDetailPage;
